import { SURFACES, surfaceAt, type SurfaceId, type SurfaceSegment } from './surfaces.js';

/**
 * Where she is.
 *
 * `RouteDef.biome` has been a string nobody reads since M2, and it showed: every route was the same green
 * hills with the same bush every seven metres. A biome is the answer to "where am I driving?", expressed
 * as data - a palette, a sky, a vocabulary of things that grow there, and which surfaces belong.
 *
 * The important part is not the list. It is that a route holds *several* biomes in bands and blends
 * between them, so a drive goes somewhere instead of scrolling. Three things blend across a transition:
 *
 *  1. the ground colour, lerped;
 *  2. the sky, crossfaded;
 *  3. **what grows** - each plant rolls against the blend to decide which biome it belongs to, so pines
 *     thin out and palms appear *through* the transition rather than switching at a line.
 *
 * The third is what actually reads as a place changing. The first two on their own look like a filter.
 *
 * Pure: colours are integers and blending is arithmetic. No Pixi, no DOM.
 */

export const BIOME_IDS = ['meadow', 'beach', 'snow', 'canyon', 'forest', 'town'] as const;
export type BiomeId = (typeof BIOME_IDS)[number];

/**
 * How tall each thing is, in metres.
 *
 * Real numbers, against a 4.5m truck, because the first draft drew every species from one base unit and
 * every "tree" came out about 0.75m - a shrub. A pre-reader knows what a tree looks like long before she
 * can name one, and a roadside of knee-high trees reads as wrong without her being able to say why.
 *
 * The renderer expresses every shape as a fraction of these, so changing one number changes one species.
 */
export const SPECIES_HEIGHT_M: Readonly<Record<SpeciesId, number>> = {
  leafy: 3.2,
  poplar: 4.4,
  pine: 3.7,
  palm: 3.9,
  bare: 2.9,
  cactus: 1.8,
  bush: 0.85,
  tuft: 0.3,
  flowers: 0.36,
  reeds: 1,
  pebble: 0.18,
  boulder: 0.55,
  log: 0.42,
  stump: 0.4,
  snowlump: 0.4,
};

/** Which layer a species belongs to. Cover goes down thickly, canopy sparsely. */
export type ScatterLayer = 'cover' | 'canopy';

/** What grows or sits on the ground. Drawn by src/drive/scenery.ts; chosen here. */
export const SPECIES_IDS = [
  'leafy',
  'poplar',
  'pine',
  'palm',
  'bare',
  'cactus',
  'bush',
  'tuft',
  'flowers',
  'reeds',
  'pebble',
  'boulder',
  'log',
  'stump',
  'snowlump',
] as const;
export type SpeciesId = (typeof SPECIES_IDS)[number];

/**
 * What a biome puts by the road. Placed by `src/core/world/structures.ts`, drawn by
 * `src/drive/structures.ts`.
 *
 * `lookout` is the exception and belongs to no biome's list: there is exactly one of it, it stands at x = 0
 * on every route, and it is the place she leaves from and comes back to rather than something the landscape
 * happens to have. Everything else here is scenery with a door on it.
 */
export const STRUCTURE_IDS = [
  'lookout',
  'barn',
  'fence',
  'silo',
  'windmill',
  'haystack',
  'cabin',
  'woodpile',
  'lighthouse',
  'beachhut',
  'jetty',
  'skipylon',
  'snowman',
  'watertower',
  'mesa',
  'house',
  'shop',
  'lamppost',
  'busstop',
] as const;
export type StructureId = (typeof STRUCTURE_IDS)[number];

export interface BiomeDef {
  readonly id: BiomeId;
  /** Deep ground fill. */
  readonly earth: number;
  /** The stripe along the very top of the ground - the bit she reads as "grass" or "sand". */
  readonly crust: number;
  /** Sky, top to bottom. Crossfaded between biomes as she drives. */
  readonly skyTop: number;
  readonly skyBottom: number;
  /** Far and mid parallax silhouettes. Distance reads as washed out, so these are pale. */
  readonly far: number;
  readonly mid: number;
  /** Foliage range: everything that grows picks a colour between these two. */
  readonly foliageDark: number;
  readonly foliageLight: number;
  readonly trunk: number;
  readonly rock: number;
  /**
   * Weighted vocabulary, in two layers. Higher weights appear more often; the sums need not be 1.
   *
   * The split is not tidiness, it is the difference between countryside and a hedge maze. Ground cover goes
   * down thickly - several things a metre - and canopy goes down sparsely, one every several metres. Drawn
   * from one list at one density, a four-metre tree arrives as often as a tuft of grass and the roadside
   * becomes a wall she cannot see past.
   */
  readonly species: {
    readonly cover: Readonly<Partial<Record<SpeciesId, number>>>;
    readonly canopy: Readonly<Partial<Record<SpeciesId, number>>>;
  };
  readonly structures: readonly StructureId[];
  /** The ground where nothing else is specified. */
  readonly defaultSurface: SurfaceId;
  /** Which patches suit this place. The route generator picks from here. */
  readonly patches: readonly SurfaceId[];
}

export const BIOMES: Readonly<Record<BiomeId, BiomeDef>> = {
  meadow: {
    id: 'meadow',
    earth: 0x7a5b3c,
    crust: 0x5aa02c,
    skyTop: 0x8fd0f0,
    skyBottom: 0xd8f0fb,
    far: 0x9fc0cf,
    mid: 0x7faa71,
    foliageDark: 0x3f8f26,
    foliageLight: 0x74c93c,
    trunk: 0x6d4f34,
    rock: 0x9a9186,
    species: {
      cover: { tuft: 10, flowers: 5, bush: 3, pebble: 2 },
      canopy: { leafy: 6, poplar: 3, boulder: 1, log: 1, stump: 1 },
    },
    structures: ['barn', 'fence', 'silo', 'windmill', 'haystack'],
    defaultSurface: 'grass',
    patches: ['mud', 'water'],
  },
  beach: {
    id: 'beach',
    earth: 0xc9a86a,
    crust: 0xe8d3a2,
    skyTop: 0x7fd3f2,
    skyBottom: 0xfdf0d0,
    far: 0xa8d8e6,
    mid: 0xd9c48a,
    foliageDark: 0x2f8f6f,
    foliageLight: 0x63c49a,
    trunk: 0x9a7345,
    rock: 0xbfae94,
    species: {
      cover: { pebble: 6, tuft: 3, bush: 2 },
      canopy: { palm: 6, boulder: 3, log: 2 },
    },
    structures: ['lighthouse', 'beachhut', 'jetty', 'fence'],
    defaultSurface: 'sand',
    patches: ['water', 'sand'],
  },
  snow: {
    id: 'snow',
    earth: 0xb9c6d2,
    crust: 0xf2fafd,
    skyTop: 0x9fbdd8,
    skyBottom: 0xe6f1f8,
    far: 0xc3d6e4,
    mid: 0x8fa8bb,
    foliageDark: 0x1f5c46,
    foliageLight: 0x3f8f6a,
    trunk: 0x5a4636,
    rock: 0x8f9aa6,
    species: {
      cover: { snowlump: 8, pebble: 2, tuft: 1 },
      canopy: { pine: 7, bare: 3, boulder: 2, stump: 1 },
    },
    structures: ['cabin', 'skipylon', 'snowman', 'woodpile', 'fence'],
    defaultSurface: 'grass',
    patches: ['ice', 'water'],
  },
  canyon: {
    id: 'canyon',
    earth: 0xa8543a,
    crust: 0xd08050,
    skyTop: 0x6fb8e0,
    skyBottom: 0xffd9a0,
    far: 0xc98a6a,
    mid: 0xa5603f,
    foliageDark: 0x5f7a34,
    foliageLight: 0x94b055,
    trunk: 0x7d5636,
    rock: 0xb06a48,
    species: {
      cover: { pebble: 8, tuft: 3, bush: 2 },
      canopy: { cactus: 6, boulder: 5, bare: 2 },
    },
    structures: ['watertower', 'mesa', 'fence'],
    defaultSurface: 'sand',
    patches: ['sand'],
  },
  forest: {
    id: 'forest',
    earth: 0x5c4a33,
    crust: 0x3f7a28,
    skyTop: 0x7ec2e2,
    skyBottom: 0xcfe8d8,
    far: 0x7d9c8a,
    mid: 0x3f6b45,
    foliageDark: 0x245c22,
    foliageLight: 0x4f9a33,
    trunk: 0x5a4028,
    rock: 0x7d8574,
    species: {
      cover: { tuft: 8, bush: 5, flowers: 3, pebble: 2, stump: 2 },
      canopy: { pine: 9, leafy: 3, log: 3, boulder: 1 },
    },
    structures: ['cabin', 'woodpile', 'fence'],
    defaultSurface: 'grass',
    patches: ['mud', 'water'],
  },
  town: {
    id: 'town',
    /*
     * Soil, not tarmac. The road is the *crust* - the thin dark band along the top - and the first draft made
     * the earth grey as well, so the whole eight-metre column below the street read as one flat slab of
     * asphalt. A road is a surface laid on ground, and it looks like one only if the ground looks like ground.
     */
    earth: 0x5a4a3c,
    crust: 0x4a4f57,
    skyTop: 0x86c8e8,
    skyBottom: 0xe4eef4,
    far: 0xa6b6c4,
    mid: 0x8a95a3,
    foliageDark: 0x3f8f26,
    foliageLight: 0x6fbf3a,
    trunk: 0x6d4f34,
    rock: 0x9a9186,
    species: {
      cover: { tuft: 4, pebble: 3, bush: 2 },
      canopy: { leafy: 4 },
    },
    structures: ['house', 'shop', 'lamppost', 'busstop'],
    defaultSurface: 'road',
    patches: ['water'],
  },
};

/** A stretch of route belonging to one biome. Bands are contiguous and cover the whole route. */
export interface BiomeBand {
  readonly fromM: number;
  readonly toM: number;
  readonly biome: BiomeId;
}

/** Two biomes and how far between them, at some point along the route. */
export interface BiomeMix {
  readonly a: BiomeId;
  readonly b: BiomeId;
  /** 0 = entirely `a`, 1 = entirely `b`. Equal ids mean it does not matter. */
  readonly t: number;
}

/**
 * Which biome is at `x`, and how much of the next one has arrived.
 *
 * The transition sits at the *end* of each band and runs `blendM` metres into the next, so a band's own
 * character holds until its border. Outside a transition `a === b` and `t` is 0, which lets every caller
 * treat "solidly somewhere" and "on the way to somewhere" identically.
 */
export function biomeAt(bands: readonly BiomeBand[], x: number, blendM: number): BiomeMix {
  if (bands.length === 0) return { a: 'meadow', b: 'meadow', t: 0 };

  let index = bands.length - 1;
  for (let i = 0; i < bands.length; i++) {
    if (x < bands[i]!.toM) {
      index = i;
      break;
    }
  }

  const band = bands[index]!;
  const next = bands[index + 1];
  if (next === undefined || blendM <= 0) {
    return { a: band.biome, b: band.biome, t: 0 };
  }

  // The zone is clamped to half the band so a very short band cannot blend past its own start, which
  // would make two transitions overlap and the middle biome never appear at full strength.
  const zone = Math.min(blendM, (band.toM - band.fromM) / 2);
  const start = band.toM - zone;
  if (x <= start) return { a: band.biome, b: band.biome, t: 0 };

  const t = smooth(Math.max(0, Math.min(1, (x - start) / Math.max(zone, 1e-6))));
  return { a: band.biome, b: next.biome, t };
}

/** Ground and sky colours at a point, with the transition already applied. */
export interface BiomePalette {
  readonly earth: number;
  readonly crust: number;
  readonly skyTop: number;
  readonly skyBottom: number;
  readonly far: number;
  readonly mid: number;
}

export function paletteAt(bands: readonly BiomeBand[], x: number, blendM: number): BiomePalette {
  const mix = biomeAt(bands, x, blendM);
  return blendPalette(BIOMES[mix.a], BIOMES[mix.b], mix.t);
}

export function blendPalette(a: BiomeDef, b: BiomeDef, t: number): BiomePalette {
  return {
    earth: lerpColour(a.earth, b.earth, t),
    crust: lerpColour(a.crust, b.crust, t),
    skyTop: lerpColour(a.skyTop, b.skyTop, t),
    skyBottom: lerpColour(a.skyBottom, b.skyBottom, t),
    far: lerpColour(a.far, b.far, t),
    mid: lerpColour(a.mid, b.mid, t),
  };
}

/**
 * Blend two 0xRRGGBB colours.
 *
 * Per channel in sRGB, which is not physically correct - a proper blend would go through linear light -
 * but these are cartoon colours a metre apart on a hillside, and sRGB interpolation is what every
 * illustrator's intuition is calibrated to. Exact at both ends, which is the part that matters.
 */
export function lerpColour(a: number, b: number, t: number): number {
  const k = Math.max(0, Math.min(1, t));
  if (k <= 0) return a;
  if (k >= 1) return b;

  const ar = (a >> 16) & 0xff;
  const ag = (a >> 8) & 0xff;
  const ab = a & 0xff;
  const br = (b >> 16) & 0xff;
  const bg = (b >> 8) & 0xff;
  const bb = b & 0xff;

  const r = Math.round(ar + (br - ar) * k);
  const g = Math.round(ag + (bg - ag) * k);
  const bl = Math.round(ab + (bb - ab) * k);
  return (r << 16) | (g << 8) | bl;
}

/** The two colours the ground is drawn with at a point. */
export interface GroundColour {
  readonly earth: number;
  readonly crust: number;
}

/**
 * What colour the ground is at `x`.
 *
 * The split is the whole point, and the first draft got it backwards. A patch - mud, a puddle, an ice
 * sheet - changes only the **crust**, the stripe along the very top that she reads as "what this is". The
 * **earth** below is always the biome's.
 *
 * Taking the patch's earth colour too meant a puddle painted the entire eight-metre column of soil beneath
 * it bright blue, which read as a hole punched through the world. Caught in a screenshot rather than by a
 * test, which is why the rule now lives here where it can be one.
 */
export function groundColourAt(
  bands: readonly BiomeBand[],
  blendM: number,
  segments: readonly SurfaceSegment[],
  x: number,
): GroundColour {
  const palette = paletteAt(bands, x, blendM);
  const patch = segments.find((segment) => x >= segment.fromM && x < segment.toM);
  return {
    earth: palette.earth,
    crust: patch === undefined ? palette.crust : SURFACES[patch.surface].crust,
  };
}

/** Which surface is underfoot, biome default included. Saves callers repeating the fallback. */
export function surfaceHere(
  bands: readonly BiomeBand[],
  segments: readonly SurfaceSegment[],
  x: number,
): SurfaceId {
  const band = biomeAt(bands, x, 0);
  return surfaceAt(segments, x, BIOMES[band.a].defaultSurface);
}

/**
 * Pick which biome a plant at `x` belongs to.
 *
 * `roll` is one number from the route's seeded generator. Rolling against the blend is what makes a
 * transition read as a place changing: at t = 0.3, three plants in ten already belong to the biome
 * ahead, so the new place arrives as a scattering rather than as a line ruled across the ground.
 */
export function speciesBiomeAt(
  bands: readonly BiomeBand[],
  x: number,
  blendM: number,
  roll: number,
): BiomeId {
  const mix = biomeAt(bands, x, blendM);
  return roll < mix.t ? mix.b : mix.a;
}

/**
 * Choose one species from a biome's weighted vocabulary, within one layer.
 *
 * Weights rather than a flat list so a meadow is mostly grass tufts with the occasional log, which is
 * what a meadow looks like. A flat list gives equal numbers of everything, which reads as a catalogue.
 */
export function pickSpecies(biome: BiomeDef, layer: ScatterLayer, roll: number): SpeciesId {
  const entries = Object.entries(biome.species[layer]) as [SpeciesId, number][];
  if (entries.length === 0) return 'tuft';

  let total = 0;
  for (const [, weight] of entries) total += weight;
  if (total <= 0) return entries[0]![0];

  let cursor = Math.max(0, Math.min(0.999999, roll)) * total;
  for (const [id, weight] of entries) {
    cursor -= weight;
    if (cursor < 0) return id;
  }
  return entries[entries.length - 1]![0];
}

/** Smoothstep, so a transition eases in and out instead of starting and stopping abruptly. */
function smooth(t: number): number {
  return t * t * (3 - 2 * t);
}
